import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  ButtonProps,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Alert,
  AlertTitle
} from '@mui/material'
import { Build } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import useDialog from '../../hooks/useDialog'
import { AxiomaW1, Uninstallable } from '../../types'
import AxiomaW1Chip from '../axioma/AxiomaW1Chip'
import { updateAsset } from '../../services/assetService'

export interface AxiomaW1UninstallButtonProps extends Omit<ButtonProps, 'children'> {
  axiomaW1: Uninstallable<AxiomaW1>
  onUninstall: (axiomaW1: AxiomaW1) => void
}

export default function AxiomaW1UninstallButton({
  axiomaW1,
  onUninstall,
  ...props
}: AxiomaW1UninstallButtonProps) {
  const { t } = useTranslation('installation')

  const { open, openDialog, closeDialog } = useDialog()

  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  const handleSubmit = async () => {
    try {
      setSubmitting(true)

      const { axiomaW1: _, ...asset } = await updateAsset(axiomaW1.asset.id, {
        name: axiomaW1.id,
        company: axiomaW1.asset.companyId,
        project: null,
        level: null,
        lift: null
      })

      axiomaW1.asset = asset
      axiomaW1.asset_id = asset.id

      onUninstall(axiomaW1)

      closeDialog()
    } catch (error) {
      console.error(error)
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    return () => {
      setSubmitting(false)
      closeDialog()
    }
  }, [])

  return (
    <>
      <Button
        variant="contained"
        color="warning"
        startIcon={<Build />}
        onClick={openDialog}
        {...props}
      >
        {t('actions:uninstall')}
      </Button>

      <Dialog open={open} maxWidth="sm" fullWidth onClose={closeDialog}>
        <DialogTitle>{t('actions:uninstall')}</DialogTitle>

        <DialogContent>
          <Alert severity="warning">
            <AlertTitle sx={{ m: 0 }}>{t('uninstallAxiomaW1Warning')}</AlertTitle>
            <AxiomaW1Chip w1={axiomaW1} color="warning" variant="outlined" sx={{ mt: 1 }} />
          </Alert>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 2 }}>
          <Button variant="text" color="inherit" onClick={closeDialog}>
            {t('actions:cancel')}
          </Button>

          <LoadingButton
            variant="contained"
            color="warning"
            loading={isSubmitting}
            onClick={handleSubmit}
          >
            {t('actions:confirm')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
